import { NotificationSettingOptionsGroup, NotificationSettingOption } from 'ComponentLibrary'
import { BlueprintIcon, CircleHalfIcon, ClipboardTextIcon, FilesIcon, HighlighterIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'

const PREFIX = 'settings.constants.notifications.groups'

const getOrdersGroupTitle = (tTerminology: TFunction) => tTerminology(`orders`)

const getPlausibilityGroupTitle = (t: TFunction, tTerminology?: TFunction) =>
    tTerminology ? tTerminology(`plausibility`) : t(`${PREFIX}.plausibility.groupTitle`)

export const getOrderNotificationSettings = (t: TFunction, tTerminology: TFunction): NotificationSettingOptionsGroup => ({
    groupTitle: getOrdersGroupTitle(tTerminology),
    titleIcon: FilesIcon,
    notificationSettingsOptions: [
        {
            title: t(`${PREFIX}.orders.newMessage.title`),
            description: t(`${PREFIX}.orders.newMessage.description`),
            options: ['batch', 'immediate', 'do_not_send'],
            identifier: 'new_message_in_thread_priority',
        },
        {
            title: t(`${PREFIX}.orders.newMention.title`),
            options: ['immediate'],
            identifier: 'new_message_in_thread_with_mention_priority',
            disabled: true,
        },
        {
            title: t(`${PREFIX}.orders.newDocument.title`),
            options: ['batch', 'do_not_send'],
            identifier: 'order_document_attached_priority',
        },
        {
            title: t(`${PREFIX}.orders.inspectionDateUpdated.title`),
            options: ['immediate', 'do_not_send'],
            identifier: 'new_inspection_date_priority',
        },
        {
            title: t(`${PREFIX}.orders.dueDateUpdated.title`),
            options: ['immediate', 'do_not_send'],
            identifier: 'new_order_due_date_priority',
        },
    ],
})

export const getOrderStatusNotificationSettings = (t: TFunction): NotificationSettingOptionsGroup => ({
    groupTitle: t(`${PREFIX}.orderStatus.groupTitle`),
    titleIcon: CircleHalfIcon,
    notificationSettingsOptions: [
        {
            title: t(`${PREFIX}.orderStatus.newStatus.title`),
            options: ['batch', 'do_not_send'],
            identifier: 'order_status_transitioned_priority',
        },
        {
            title: t(`${PREFIX}.orderStatus.accepted.title`),
            description: t(`${PREFIX}.orderStatus.accepted.description`),
            options: ['immediate', 'do_not_send'],
            identifier: 'order_accepted_priority',
        },
        {
            title: t(`${PREFIX}.orderStatus.blocked.title`),
            description: t(`${PREFIX}.orderStatus.blocked.description`),
            options: ['immediate', 'do_not_send'],
            identifier: 'order_blocked_priority',
        },
    ],
})

export const getValuationAndDraftsNotificationSettings = (t: TFunction): NotificationSettingOptionsGroup => ({
    groupTitle: t(`${PREFIX}.valuations.groupTitle`),
    titleIcon: BlueprintIcon,
    notificationSettingsOptions: [
        {
            title: t(`${PREFIX}.valuations.newDraft.title`),
            options: ['immediate', 'batch', 'do_not_send'],
            identifier: 'draft_valuation_created_priority',
        },
        {
            title: t(`${PREFIX}.valuations.finalisedValuation.title`),
            options: ['immediate', 'do_not_send'],
            identifier: 'valuation_finalised_priority',
        },
        {
            title: t(`${PREFIX}.valuations.finalisedValueCheck.title`),
            options: ['batch', 'do_not_send'],
            identifier: 'value_check_finalised_priority',
        },
    ],
})

export const getMyTasksNotificationSettings = (t: TFunction): NotificationSettingOptionsGroup => ({
    groupTitle: t(`${PREFIX}.myTasks.groupTitle`),
    titleIcon: ClipboardTextIcon,
    notificationSettingsOptions: [
        {
            title: t(`${PREFIX}.myTasks.assigned.title`),
            options: ['batch', 'do_not_send'],
            identifier: 'new_task_assignee_in_my_task_priority',
        },
        {
            title: t(`${PREFIX}.myTasks.statusChanged.title`),
            options: ['batch', 'do_not_send'],
            identifier: 'my_task_status_transitioned_priority',
        },
        {
            title: t(`${PREFIX}.myTasks.newComment.title`),
            options: ['batch', 'immediate', 'do_not_send'],
            identifier: 'new_message_in_thread_connected_to_my_task_priority',
        },
        {
            title: t(`${PREFIX}.myTasks.dueSoon.title`),
            description: t(`${PREFIX}.myTasks.dueSoon.description`),
            options: ['batch', 'do_not_send'],
            identifier: 'task_due_soon_priority',
        },
        {
            title: t(`${PREFIX}.myTasks.dueTomorrow.title`),
            options: ['batch', 'do_not_send'],
            identifier: 'task_due_tomorrow_priority',
        },
    ],
})

export const getOtherTasksNotificationSettings = (t: TFunction): NotificationSettingOptionsGroup => ({
    groupTitle: t(`${PREFIX}.otherTasks.groupTitle`),
    titleIcon: ClipboardTextIcon,
    notificationSettingsOptions: [
        {
            title: t(`${PREFIX}.otherTasks.created.title`),
            options: ['batch', 'do_not_send'],
            identifier: 'task_created_priority',
        },
        {
            title: t(`${PREFIX}.otherTasks.statusChanged.title`),
            options: ['batch', 'do_not_send'],
            identifier: 'task_status_transitioned_priority',
        },
        {
            title: t(`${PREFIX}.otherTasks.newComment.title`),
            description: t(`${PREFIX}.otherTasks.newComment.description`),
            options: ['batch', 'immediate', 'do_not_send'],
            identifier: 'new_message_in_thread_connected_to_task_priority',
        },
    ],
})

export const getPlausibilityNotificationSettingsBase = (t: TFunction, tTerminology?: TFunction): NotificationSettingOptionsGroup => ({
    groupTitle: getPlausibilityGroupTitle(t, tTerminology),
    titleIcon: HighlighterIcon,
    notificationSettingsOptions: [
        {
            title: t(`${PREFIX}.plausibility.newMessage.title`),
            options: ['batch', 'immediate', 'do_not_send'],
            identifier: 'plausibility_new_message_priority',
        },
        {
            title: t(`${PREFIX}.plausibility.editedMessage.title`),
            options: ['batch', 'immediate', 'do_not_send'],
            identifier: 'plausibility_edited_message_priority',
        },
        {
            title: t(`${PREFIX}.plausibility.deletedMessage.title`),
            options: ['batch', 'immediate', 'do_not_send'],
            identifier: 'plausibility_deleted_message_priority',
        },
        {
            title: t(`${PREFIX}.plausibility.shared.title`),
            options: ['batch', 'immediate', 'do_not_send'],
            identifier: 'plausibility_handed_over_priority',
        },
        {
            title: t(`${PREFIX}.plausibility.approved.title`),
            options: ['batch', 'immediate', 'do_not_send'],
            identifier: 'plausibility_approved_priority',
        },
    ],
})

const getPlausibilityInternalApprovalNotificationSetting = (t: TFunction): NotificationSettingOption => ({
    title: t(`${PREFIX}.plausibility.internalApproved.title`),
    options: ['batch', 'immediate', 'do_not_send'],
    identifier: 'plausibility_internally_approved_priority',
})

export const getPlausibilityNotificationSettings = (
    isPlausibilityInternalApprovalEnabled: boolean,
    t: TFunction,
    tTerminology?: TFunction
): NotificationSettingOptionsGroup => ({
    ...getPlausibilityNotificationSettingsBase(t, tTerminology),
    notificationSettingsOptions: isPlausibilityInternalApprovalEnabled
        ? [
              ...getPlausibilityNotificationSettingsBase(t, tTerminology).notificationSettingsOptions,
              getPlausibilityInternalApprovalNotificationSetting(t),
          ]
        : getPlausibilityNotificationSettingsBase(t, tTerminology).notificationSettingsOptions,
})
